import { useSelector } from 'react-redux';

import * as moment from 'moment';
import SubscriptionStore from '../../../redux/subscriptions';
import { window } from '../../../globals';
import { apiPriceToPlanNameMap, fossaPlanMap, PREMIUM_PER_REVISION_PLAN_NAME } from '../../../../shared/subscriptions';
import type { StripePlanMapItem } from '../../../../shared/subscriptions';
import { useDispatchOnMount } from '../../../hooks';

import Loader from '../../generic/Loader';
import InfoPanel from '../../generic/InfoPanel';
import { icons } from '../../generic/Icon/icons';
import { MailToLink } from '../../generic/Link';
import type { FossaSubscriptionPlan } from '../../../types';

const MAX_CONTRIBUTOR_COUNT = 150;

type SubscriptionInfoDetailProps = {
  title: string;
  value: string;
  isLoading?: boolean;
};

const LittleLoader = () => {
  return (
    <Loader
      size={25}
      style={{
        textAlign: 'left',
      }}
    />
  );
};

/**
 * This function is used to retrieve the full Fossa Stripe Plan object from featureFlags.
 * @todo Remove apiPriceToPlanNameMap usage once no orgs are using API price names.
 * @returns {StripePlanMapItem | null} The fossaPlanMap object or null if it doesn't exist.
 */
const getCurrentFossaPlan = (): StripePlanMapItem | null => {
  let orgPlan = window.user?.organization.stripe_plan;
  if (!orgPlan) {
    return null;
  }

  // The plan name could be the API price name. Try to map to a plaintext plan name.
  // TODO: Remove this once every org has the plaintext plan name in the db.
  if (!Object.keys(fossaPlanMap).includes(orgPlan)) {
    orgPlan = apiPriceToPlanNameMap[orgPlan];
    if (!orgPlan) {
      return null;
    }
  }

  return fossaPlanMap[orgPlan] ?? null;
};

/**
 * Determines the string to use to display the price. If the org doesn't have a free plan or custom_billing,
 * it should return the total price for the organization in the format `$price/mo`.
 * @param devCount The number of developers the current organization has.
 * @returns The string to display for the price.
 */
const getPrice = (devCount: number): string => {
  if (window.user?.organization.custom_billing) {
    return 'Custom';
  }

  const orgPlan = getCurrentFossaPlan();

  if (!orgPlan) {
    return '-';
  }

  if (orgPlan.price === 0) {
    return 'Free';
  }

  return `$${orgPlan.price * devCount}/mo`;
};

const getUsage = (subscription: FossaSubscriptionPlan) => {
  // Return revision usage for per-revision plans.
  if (subscription.plan === PREMIUM_PER_REVISION_PLAN_NAME) {
    if (!subscription.usage) {
      return 'There is no billing term for this organization. Please contact your account manager.';
    }

    const planLimit = subscription.usage.quantity;
    const usage = subscription.usage.usage;

    const termStart = moment(subscription.usage.termStart).utc().format('MM/DD/YYYY');
    const termEnd = moment(subscription.usage.termEnd).utc().format('MM/DD/YYYY');

    return `${usage} of ${planLimit} revisions used. (${termStart} - ${termEnd})`;
  }

  // Otherwise return contributor usage.
  const contributorCount = subscription.contributor_count;
  if (contributorCount > MAX_CONTRIBUTOR_COUNT) {
    return '150+ code contributors tracked';
  }

  return `${contributorCount} code contributor${contributorCount !== 1 ? 's' : ''} tracked`;
};

const getPlanName = () => {
  const orgPlan = getCurrentFossaPlan();
  return orgPlan ? `${orgPlan.label}: ${orgPlan.description}` : '-';
};

const SubscriptionInfoDetail = ({ title, value, isLoading = false }: SubscriptionInfoDetailProps) => (
  <div>
    <div className="detail-title">{title}</div>
    {isLoading ? <LittleLoader /> : <div className="detail-value">{value}</div>}
  </div>
);

const BillingHeader = () => {
  const subscription = useSelector(SubscriptionStore.selectors.getSubscription);
  const { isLoading } = useSelector(SubscriptionStore.selectors.getLoading);

  useDispatchOnMount(SubscriptionStore.actions.fetchSubscription);

  // Only show for non-enterprise orgs that have exceeded the cap.
  const shouldShowUsageCap =
    !window.user?.organization.custom_billing &&
    subscription &&
    subscription.contributor_count > MAX_CONTRIBUTOR_COUNT;

  return (
    <div className="subscription-info">
      <div className="subscription-info-title">Subscription</div>
      <div className="current-subscription-container">
        <SubscriptionInfoDetail title="Customer" value={window.user?.organization.title ?? '-'} />
        <SubscriptionInfoDetail title="Current Plan" value={getPlanName()} />
        <SubscriptionInfoDetail
          title="Current Plan Price"
          value={subscription ? getPrice(subscription.quantity) : '-'}
          isLoading={!subscription || isLoading}
        />
        <SubscriptionInfoDetail
          title="Usage"
          value={subscription ? getUsage(subscription) : '-'}
          isLoading={!subscription || isLoading}
        />
      </div>
      {shouldShowUsageCap && (
        <InfoPanel icon={icons.exclamationCircle}>
          You have exceeded our usage cap of 150 developers. You will not be billed beyond this, but a member
          from our team will contact you shortly about a custom plan.
        </InfoPanel>
      )}
      {subscription && (
        <div className="alert">
          {subscription.plan === PREMIUM_PER_REVISION_PLAN_NAME ? (
            <>
              FOSSA automatically meters based on the number of revisions made to your private imported repos.
            </>
          ) : (
            <>
              FOSSA automatically meters based off unique contributors to your private imported repos.
            </>
          )}
          {' '}Something off? <MailToLink email="support@fossa.com">Contact support</MailToLink>.
        </div>
      )}
    </div>
  );
};

export default BillingHeader;
